import { useState } from "react";
import { History, ChevronDown, ChevronUp, Info, ExternalLink } from "lucide-react";
import { AlertRow } from "./AlertRow";
import { fmtDate, fmtAge } from "@/lib/format";
import { useFloatingCards } from "@/context/FloatingCards";
import { LocationDetailCard } from "@/components/cards/LocationDetailCard";

export function LocationContext({ context, onOpenIncident }) {
  const [expanded, setExpanded] = useState(false);
  const cards = useFloatingCards();
  if (!context) return null;
  const { record, prior_incidents: priors, alerts = [] } = context;
  if (!record && (!priors || priors.length === 0)) return null;
  const last = priors?.[0];
  return (
    <div className="rounded-md border bg-muted/40 px-2.5 py-1.5 text-xs space-y-1" data-testid="location-context">
      {alerts.length > 0 && (
        <div className="space-y-1" data-testid="location-alerts">
          {alerts.map((a) => <AlertRow key={a.id} a={a} compact />)}
        </div>
      )}
      {record && (
        <div className="flex items-start gap-1.5" data-testid="location-record-note">
          <Info className="h-3.5 w-3.5 text-teal-500 mt-0.5 shrink-0" />
          <div className="flex-1"><span className="font-medium">{record.common_name || record.raw}</span>{record.notes && <span className="text-muted-foreground"> — {record.notes}</span>}</div>
          <button data-testid="location-record-open" onClick={() => cards.open(`location-${record.id}`, LocationDetailCard, { recordId: record.id, onOpenIncident }, { width: 640 })} className="text-primary hover:underline flex items-center gap-0.5 shrink-0"><ExternalLink className="h-3 w-3" /> Location record</button>
        </div>
      )}
      {last && (
        <div className="flex items-center gap-1.5" data-testid="location-prior-summary">
          <History className="h-3.5 w-3.5 text-amber-500 shrink-0" />
          <span>Last incident here: <span className="mono">{fmtDate(last.received_at)}</span> ({fmtAge(last.received_at)} ago) — <span className="font-medium">{last.call_type_name}</span>{last.disposition_label ? ` · ${last.disposition_label}` : ""}</span>
          <button data-testid="location-prior-toggle" onClick={() => setExpanded((e) => !e)} className="ml-auto text-primary hover:underline flex items-center gap-0.5">
            {priors.length} prior {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          </button>
        </div>
      )}
      {expanded && (
        <div className="pl-5 space-y-0.5" data-testid="location-prior-list">
          {priors.map((p) => (
            <button key={p.id} onClick={() => onOpenIncident(p.id)} className="block w-full text-left hover:underline">
              <span className="mono text-primary">{p.incident_number}</span> · {fmtDate(p.received_at)} · {p.call_type_name} · {p.disposition_label || p.status.toUpperCase()}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
